import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Pencil, Plus, DoorOpen, ShieldCheck, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { get, post, patch, del, getErrorMessage } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { formatDateTime } from '@/lib/utils';

interface Gate {
  id: string;
  society_id: string;
  name: string;
  description: string | null;
  is_primary: boolean;
  active: boolean;
  created_at: string;
  updated_at: string;
  _count?: { guards: number };
}

interface GateListResponse {
  items: Gate[];
}

const CreateGateSchema = z.object({
  name: z.string().min(1, 'Name required').max(80),
  description: z.string().max(200).optional(),
  is_primary: z.boolean().default(false)
});
type CreateGateForm = z.infer<typeof CreateGateSchema>;

export function GatesPage(): JSX.Element {
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [removeTarget, setRemoveTarget] = useState<Gate | null>(null);
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['society', 'gates'],
    queryFn: () => get<GateListResponse>('/society/gates')
  });

  const gates = query.data?.items ?? [];
  const editingGate = useMemo(
    () => (editingId ? gates.find((g) => g.id === editingId) ?? null : null),
    [editingId, gates]
  );

  const form = useForm<CreateGateForm>({
    resolver: zodResolver(CreateGateSchema),
    defaultValues: { name: '', description: '', is_primary: false }
  });

  const create = useMutation({
    mutationFn: (values: CreateGateForm) =>
      post<Gate, CreateGateForm>('/society/gates', {
        ...values,
        description: values.description?.trim() ? values.description : undefined
      }),
    onSuccess: (g) => {
      toast.success(`Gate "${g.name}" added`);
      void queryClient.invalidateQueries({ queryKey: ['society', 'gates'] });
      setOpen(false);
      form.reset();
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const update = useMutation({
    mutationFn: ({ id, values }: { id: string; values: CreateGateForm }) =>
      patch<Gate>(`/society/gates/${id}`, {
        ...values,
        description: values.description?.trim() ? values.description : null
      }),
    onSuccess: (g) => {
      toast.success(`Gate "${g.name}" updated`);
      void queryClient.invalidateQueries({ queryKey: ['society', 'gates'] });
      setEditingId(null);
      setOpen(false);
      form.reset();
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const remove = useMutation({
    mutationFn: (id: string) => del<void>(`/society/gates/${id}`),
    onSuccess: () => {
      toast.success('Gate removed');
      void queryClient.invalidateQueries({ queryKey: ['society', 'gates'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  useEffect(() => {
    if (editingGate && open) {
      form.reset({
        name: editingGate.name,
        description: editingGate.description ?? '',
        is_primary: editingGate.is_primary
      });
    } else if (!open && !editingId) {
      form.reset({ name: '', description: '', is_primary: false });
    }
  }, [editingGate, open, editingId, form]);

  const onSubmit = (v: CreateGateForm) => {
    if (editingId) update.mutate({ id: editingId, values: v });
    else create.mutate(v);
  };

  const closeDialog = () => {
    setOpen(false);
    setEditingId(null);
  };

  const formDialog = (
    <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : closeDialog())}>
      <DialogTrigger asChild>
        <Button size="sm" onClick={() => setEditingId(null)}>
          <Plus className="h-4 w-4" /> Add gate
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editingId ? 'Edit gate' : 'Add gate'}</DialogTitle>
          <DialogDescription>
            Define entry/exit points (Main Gate, Side Gate, Back Gate). Guards are assigned to gates separately.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-vt-4">
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              placeholder="Main Gate"
              autoFocus
              aria-invalid={Boolean(form.formState.errors.name) || undefined}
              {...form.register('name')}
            />
            {form.formState.errors.name ? (
              <p className="text-vt-12 text-vt-alert" role="alert">{form.formState.errors.name.message}</p>
            ) : (
              <p className="text-vt-12 text-vt-text-soft">Must be unique within the society.</p>
            )}
          </div>
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="description">Description (optional)</Label>
            <Input
              id="description"
              placeholder="Primary entry from main road"
              {...form.register('description')}
            />
          </div>
          <label className="flex items-center gap-vt-2 text-vt-13 text-vt-text">
            <input type="checkbox" {...form.register('is_primary')} className="h-4 w-4" />
            Primary gate (default entry for visitors)
          </label>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={closeDialog}>
              Cancel
            </Button>
            <Button
              type="submit"
              loading={create.isPending || update.isPending}
              loadingText={editingId ? 'Saving…' : 'Adding…'}
            >
              {editingId ? 'Save changes' : 'Add gate'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );

  const totalGuards = gates.reduce((s, g) => s + (g._count?.guards ?? 0), 0);

  return (
    <>
      <PageHeader
        title="Gates"
        description="Entry / exit points of the society. Assign guards to specific gates from the Guards page."
        actions={formDialog}
      />
      <div className="mx-auto w-full max-w-[1400px] space-y-vt-4 px-vt-6 pb-vt-8 pt-vt-4">
        {/* KPI strip */}
        <div className="grid grid-cols-1 gap-vt-4 md:grid-cols-3">
          <Card>
            <CardContent className="flex items-center gap-vt-3 p-vt-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-vt-md bg-vt-primary-100 text-vt-primary">
                <DoorOpen className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <div>
                <div className="text-vt-12 text-vt-text-muted">Total gates</div>
                <div className="text-vt-24 font-semibold text-vt-text">
                  {query.isLoading ? <Skeleton className="h-7 w-12" /> : gates.length}
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-vt-3 p-vt-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-vt-md bg-[color:var(--vt-info-100)] text-[color:var(--vt-info)]">
                <ShieldCheck className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <div>
                <div className="text-vt-12 text-vt-text-muted">Guards posted</div>
                <div className="text-vt-24 font-semibold text-vt-text">
                  {query.isLoading ? <Skeleton className="h-7 w-12" /> : totalGuards}
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-vt-3 p-vt-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-vt-md bg-[color:var(--vt-safe-100)] text-[color:var(--vt-safe)]">
                <DoorOpen className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <div>
                <div className="text-vt-12 text-vt-text-muted">Primary gate</div>
                <div className="text-vt-16 font-semibold text-vt-text">
                  {query.isLoading ? (
                    <Skeleton className="h-6 w-20" />
                  ) : (
                    gates.find((g) => g.is_primary)?.name ?? '—'
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-vt-4">
            {query.isLoading ? (
              <div className="flex flex-col gap-vt-2">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : query.isError ? (
              <EmptyState
                icon={<DoorOpen className="h-6 w-6" strokeWidth={1.75} />}
                title="Could not load gates"
                description={getErrorMessage(query.error)}
                action={
                  <Button variant="secondary" onClick={() => void query.refetch()}>
                    Try again
                  </Button>
                }
              />
            ) : gates.length === 0 ? (
              <EmptyState
                icon={<DoorOpen className="h-6 w-6" strokeWidth={1.75} />}
                title="No gates defined"
                description="Define entry/exit points so you can assign guards to each."
                action={
                  <Button onClick={() => setOpen(true)}>
                    <Plus className="h-4 w-4" /> Add first gate
                  </Button>
                }
              />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Gate</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-right">Guards</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Added</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {gates.map((g) => {
                    const guardCount = g._count?.guards ?? 0;
                    return (
                      <TableRow key={g.id}>
                        <TableCell>
                          <div className="flex items-center gap-vt-3">
                            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-vt-sm bg-vt-primary-100 text-vt-12 font-semibold text-vt-primary">
                              <DoorOpen className="h-4 w-4" />
                            </span>
                            <div className="min-w-0">
                              <div className="flex items-center gap-vt-2">
                                <span className="font-medium text-vt-text">{g.name}</span>
                                {g.is_primary ? <Badge variant="info">Primary</Badge> : null}
                              </div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-vt-text-muted">{g.description ?? '—'}</TableCell>
                        <TableCell className="text-right font-mono text-vt-13 text-vt-text">{guardCount}</TableCell>
                        <TableCell>
                          {g.active ? (
                            <Badge variant="ok">Active</Badge>
                          ) : (
                            <Badge variant="muted">Inactive</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-vt-text-muted">{formatDateTime(g.created_at)}</TableCell>
                        <TableCell className="text-right">
                          <div className="inline-flex items-center gap-vt-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              title="Edit"
                              onClick={() => {
                                setEditingId(g.id);
                                setOpen(true);
                              }}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={guardCount > 0 || remove.isPending}
                              title={guardCount > 0 ? `${guardCount} guard(s) assigned — reassign first` : 'Delete'}
                              onClick={() => setRemoveTarget(g)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      <ConfirmDialog
        open={Boolean(removeTarget)}
        onOpenChange={(v) => (v ? null : setRemoveTarget(null))}
        title="Delete gate"
        description={
          removeTarget ? (
            <>
              Delete gate <strong>{removeTarget.name}</strong>? This action cannot be undone.
            </>
          ) : null
        }
        confirmLabel="Delete gate"
        variant="danger"
        loading={remove.isPending}
        onConfirm={() => {
          if (!removeTarget) return;
          remove.mutate(removeTarget.id, { onSuccess: () => setRemoveTarget(null) });
        }}
      />
    </>
  );
}
